import { PATHWAY } from "@/constants/testIds";
import { AI_NOTICE, REFERENCE_SCENARIO } from "@/lib/pathwayData";
import { SectionEyebrow } from "@/components/pathway/Journey";
import {
  ConfidenceCard,
  ExceptionLoop,
  GovernanceStateCard,
} from "@/components/pathway/GovernanceState";

export default function ReferenceScenario({ onGenerateReceipt }) {
  return (
    <section id="scenario" className="border-t hairline bg-[#0a0a0a]">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <SectionEyebrow>Reference scenario · operational efficiency</SectionEyebrow>
        <h2 className="mt-4 max-w-4xl font-serif text-3xl leading-tight tracking-tight text-neutral-50 sm:text-4xl lg:text-5xl">
          {REFERENCE_SCENARIO.title}.
        </h2>
        <div className="mt-3 font-mono text-xs uppercase tracking-[0.24em] text-neutral-500">
          {REFERENCE_SCENARIO.subtitle}
        </div>
        <p className="mt-8 max-w-3xl text-[16px] leading-relaxed text-neutral-300">
          {REFERENCE_SCENARIO.lede}
        </p>

        <div className="mt-6 inline-block border hairline px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">
          {AI_NOTICE}
        </div>

        {/* Exception loop strip — the differentiator */}
        <div className="mt-10">
          <ExceptionLoop />
        </div>

        {/* Two-column: walkthrough + sticky governance state */}
        <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-[1fr_320px] md:gap-12">
          <ol className="border-l hairline">
            {REFERENCE_SCENARIO.steps.map((s, i) => (
              <li
                key={s.n}
                data-testid={PATHWAY.scenarioWalkthroughStep(i + 1)}
                className="relative pl-8 pb-12 md:pl-10"
              >
                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 bg-amber-600" />
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-600">
                  Step {s.n} · {s.label}
                </div>
                <p className="mt-4 max-w-3xl font-serif text-lg italic leading-snug text-neutral-100">
                  {s.narrative}
                </p>

                <div className="mt-6 grid gap-6 md:grid-cols-3">
                  <Beat label="Why this matters" body={s.whyMatters} />
                  <Beat
                    label="Why AI alone is insufficient"
                    body={s.whyAIInsufficient}
                  />
                  <Beat
                    label="Where governance becomes important"
                    body={s.whereGovernance}
                  />
                </div>

                {s.successLooksLike && (
                  <div className="mt-6 border-t hairline pt-5 md:max-w-3xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
                      What success looks like
                    </div>
                    <p className="mt-2 text-[15px] leading-relaxed text-neutral-300">
                      {s.successLooksLike}
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ol>

          <div className="space-y-6 md:sticky md:top-24 md:self-start">
            <GovernanceStateCard />
            <ConfidenceCard />
          </div>
        </div>

        <div className="mt-6 flex flex-col items-start gap-4 border-t hairline pt-10 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl text-sm leading-relaxed text-neutral-400">
            Generate a simulated operational receipt for this reference
            scenario. Open the <em>View API</em> tab to see the shape of a
            runtime call in a live KATLAS deployment.
          </div>
          <button
            type="button"
            onClick={onGenerateReceipt}
            data-testid={PATHWAY.scenarioGenerateReceipt}
            className="border border-[#7F1D1D] bg-[#7F1D1D] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors hover:bg-[#991B1B]"
          >
            Generate operational receipt
          </button>
        </div>
      </div>
    </section>
  );
}

function Beat({ label, body }) {
  return (
    <div className="border-l hairline pl-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">
        {label}
      </div>
      <p className="mt-2 text-[14px] leading-relaxed text-neutral-300">{body}</p>
    </div>
  );
}
